import { AlertBanner, Heading, Text } from "@otrium/atoms";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import {
  ButtonAddCoupon,
  ButtonDeleteCoupon,
  CartOrderSummaryContent,
} from "src/organisms/CartView/CartView.styled";
import { t, Trans } from "@lingui/macro";
import { cartCountHelper } from "src/utils/ct/cartCountHelper";
import CTPrice from "src/atoms/CTPrice/CTPrice";
import {
  getOtriumSavings,
  getTotalSaved,
  lineItemsTotalPrice,
} from "src/utils/ct/totalPrice";
import { isShippingPriceFree } from "src/utils/ct/shippingMethodPrice";
import { SummaryCustomLineItem } from "src/molecules/SummaryCustomLineItem";
import { Cart, DiscountCodeState, Reference } from "src/types/ctgraphql.d";
import { DeleteOutline, Promotional } from "src/icons";
import { Divider } from "src/atoms/Divider";
import Input from "src/atoms/Input";
import { Button } from "src/atoms/Button";
import { Error } from "src/atoms/Input/Input.styled";
import { DISCOUNT_ERROR_CODES } from "src/organisms/CartView/utils";
import { InlineHyperLink } from "src/molecules/SubscribeOverlay/SubscribeModal.styled";
import { ButtonCheckout } from "src/organisms/CartView/ButtonCheckout";
import { PaymentMethodsInfo } from "src/molecules/PaymentMethodsInfo";
import { RRPFootnote } from "src/molecules/RRPFootnote";
import {
  ChangeEvent,
  KeyboardEvent,
  FC,
  useContext,
  useMemo,
  useState,
} from "react";
import { Theme, useTheme } from "@emotion/react";
import { AppCtx } from "src/contexts/app.context";
import { useCheckoutMessage } from "src/hooks/useCheckoutMessage";
import { useLingui } from "@lingui/react";
import { CartLineItemsDiscount } from "src/atoms/CartLineItemsDiscount";

interface CartViewOrderSummaryProps {
  isDisabled: boolean;
  cart: Cart | null;
  onRemoveDiscountCodeFromCart: (discountCodeRef: Reference) => void;
  couponInput: string;
  couponWarnings: string[];
  addDiscountCodeToCartLoading: boolean;
  onAddDiscountCodeToCart: () => void;
  onCouponInputUpdate: (event: ChangeEvent<HTMLInputElement>) => void;
  onCouponInputEnter: (event: KeyboardEvent<HTMLInputElement>) => void;
  hasOutOfStockItems: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isEmptyCart: boolean;
}

const CartViewOrderSummary: FC<CartViewOrderSummaryProps> = ({
  isDisabled,
  cart,
  onRemoveDiscountCodeFromCart,
  couponInput,
  couponWarnings,
  addDiscountCodeToCartLoading,
  onAddDiscountCodeToCart,
  onCouponInputUpdate,
  onCouponInputEnter,
  hasOutOfStockItems,
  setIsOpen,
  isEmptyCart,
}) => {
  const { i18n } = useLingui();
  const theme: Theme = useTheme();
  const { storeConfig } = useContext(AppCtx);
  const totalSaved = useMemo(
    () => getTotalSaved(cart, storeConfig),
    [cart, storeConfig]
  );
  const [showCouponInput, setShowCouponInput] = useState(false);

  const { data: checkoutMessageData } = useCheckoutMessage();
  const checkoutMessage = checkoutMessageData?.miscellaneous.checkoutMessage;

  const { isMobile } = useBreakpoints();
  const otriumSavings = useMemo(
    () => getOtriumSavings(cart?.lineItems || [], cart?.discountCodes || []),
    [cart?.lineItems, cart?.discountCodes]
  );

  return (
    <>
      <Box>
        <CartOrderSummaryContent
          p={3}
          py={5}
          px={[3, 3, 3, 5]}
          isDisabled={isDisabled}
        >
          <Heading as="h4" variant="h4">
            <Trans>Order summary</Trans>
          </Heading>
          <Box
            mt={3}
            fontSize={0}
            sx={{
              lineHeight: `${theme.fontSizes[2]}px`,
              letterSpacing: "0.004em",
            }}
          >
            {cart?.lineItems?.length && (
              <Flex
                data-testid="cart-order-summary-total-items"
                justifyContent="space-between"
              >
                <Box>
                  <Trans>Total items</Trans> ({cartCountHelper(cart.lineItems)})
                </Box>
                <Box data-testid="cart-order-summary-total-items-price">
                  {cart?.lineItems && (
                    <CTPrice price={lineItemsTotalPrice(cart.lineItems)} />
                  )}
                </Box>
              </Flex>
            )}

            {cart?.shippingInfo?.price && (
              <Flex
                data-testid="cart-order-summary-shipping-and-processing"
                mt={2}
                justifyContent="space-between"
              >
                <Box>
                  <Trans>summary.shipping_fee</Trans>
                </Box>
                <Box data-testid="cart-order-summary-shipping-and-processing-fee">
                  {isShippingPriceFree(cart) ? (
                    <Trans>Free</Trans>
                  ) : (
                    <CTPrice
                      price={
                        cart.shippingInfo.discountedPrice?.value ||
                        cart?.shippingInfo?.price
                      }
                    />
                  )}
                </Box>
              </Flex>
            )}

            {cart?.customLineItems.map((customLineItem, index) => (
              <Flex key={index} mt={2}>
                <SummaryCustomLineItem
                  dataTestIdPrefix="cart-order-summary-"
                  customLineItem={customLineItem}
                />
              </Flex>
            ))}

            {cart?.shippingAddress?.country === "US" && (
              <Flex
                data-testid="cart-order-summary-state-taxes"
                mt={2}
                justifyContent="space-between"
              >
                <Box>
                  <Trans>State taxes</Trans>
                </Box>
                <Box>
                  {cart.taxedPrice?.totalTax ? (
                    <CTPrice price={cart.taxedPrice?.totalTax} />
                  ) : (
                    <Trans>To be calculated</Trans>
                  )}
                </Box>
              </Flex>
            )}

            {otriumSavings?.centAmount ? (
              <Flex
                data-testid="cart-order-summary-state-taxes"
                mt={2}
                justifyContent="space-between"
                sx={{ color: theme.colors.semantic.red }}
              >
                <Box>
                  <Trans>Otrium savings</Trans>
                </Box>
                <Box>
                  - <CTPrice price={otriumSavings} />
                </Box>
              </Flex>
            ) : null}

            {cart?.discountCodes?.map(
              (discountCode, index) =>
                discountCode.state === DiscountCodeState.MatchesCart && (
                  <Flex
                    key={index}
                    mt={2}
                    justifyContent="space-between"
                    sx={{ color: theme.colors.tone.anthraciteLight }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        placeItems: "center",
                        gap: theme.space.space4,
                      }}
                    >
                      <Trans>Promo code</Trans>:{" "}
                      <Text
                        data-testid="cart-order-summary-coupon-code"
                        as="span"
                        fontWeight="600"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {discountCode.discountCode?.code}
                      </Text>
                      <ButtonDeleteCoupon
                        data-testid="cart-delete-coupon-button"
                        aria-label="Remove coupon"
                        onPress={() =>
                          onRemoveDiscountCodeFromCart(
                            discountCode.discountCodeRef
                          )
                        }
                      >
                        <DeleteOutline />
                      </ButtonDeleteCoupon>
                    </Box>

                    <CartLineItemsDiscount
                      discountCodes={[discountCode]}
                      lineItems={cart.lineItems}
                    />
                  </Flex>
                )
            )}
          </Box>

          <Box mt={4}>
            <Divider color={theme.colors.tone.marble} />
          </Box>

          {(cart?.taxedPrice?.totalGross || cart?.totalPrice) && (
            <Text
              mt={4}
              fontSize={1}
              fontWeight="bold"
              lineHeight={`${theme.fontSizes[3]}px`}
              letterSpacing="0.004em"
            >
              <Flex justifyContent="space-between">
                <Box>
                  <Trans>Total amount</Trans>:
                </Box>
                <Box data-testid="cart-order-summary-total-amount">
                  <CTPrice
                    price={cart.taxedPrice?.totalGross || cart.totalPrice}
                  />
                </Box>
              </Flex>
            </Text>
          )}

          {totalSaved && (
            <Flex
              mt={2}
              justifyContent="space-between"
              sx={{
                lineHeight: `${theme.fontSizes[2]}px`,
                letterSpacing: "0.004em",
              }}
            >
              <Box>
                <Text
                  variant="body.source.regular14"
                  sx={{
                    color: theme.colors.semantic.red,
                  }}
                >
                  <Trans>Total saved</Trans>
                </Text>
                <Text
                  variant="body.source.regular12"
                  color="tone.anthraciteLight"
                  mt={1}
                >
                  <Trans>Compared to the recommended retail price</Trans>
                </Text>
              </Box>
              <Box>
                <Text
                  variant="body.source.regular14"
                  sx={{
                    color: theme.colors.semantic.red,
                  }}
                >
                  <CTPrice
                    data-testid="cart-order-summary-total-saved"
                    price={totalSaved}
                  />
                </Text>
              </Box>
            </Flex>
          )}

          <Box mt={4}>
            <Divider color={theme.colors.tone.marble} />
          </Box>

          {!cart?.discountCodes.length && (
            <Flex mt={4} data-testid="cart-add-coupon-input-wrapper">
              {showCouponInput ? (
                <Flex flexDirection="column" flexGrow={1}>
                  <Text
                    fontSize={1}
                    mb={2}
                    fontWeight="bold"
                    lineHeight={`${theme.fontSizes[3]}px`}
                    letterSpacing="0.004em"
                    as="label"
                    htmlFor="add-promo-code-input"
                  >
                    <Trans>Add promo code</Trans>:
                  </Text>
                  <Flex flexDirection="row">
                    <Box sx={{ flexGrow: 1 }}>
                      <Input
                        isError={couponWarnings.length > 0}
                        id="add-promo-code-input"
                        backgroundColor="White"
                        placeholder={t(i18n)`e.g. HNOQW12`}
                        value={couponInput}
                        disabled={addDiscountCodeToCartLoading}
                        onChange={onCouponInputUpdate}
                        onKeyUp={onCouponInputEnter}
                        maxLength={30}
                        sx={{
                          borderRadius: "4px",
                        }}
                      />
                    </Box>
                    <Box ml={2} minWidth="104px">
                      <Button
                        data-testid="cart-add-coupon-apply-button"
                        variant="secondary"
                        disabled={!couponInput || addDiscountCodeToCartLoading}
                        loading={addDiscountCodeToCartLoading}
                        onClick={onAddDiscountCodeToCart}
                        fullWidth
                      >
                        <Trans>Apply</Trans>
                      </Button>
                    </Box>
                  </Flex>
                  <div>
                    {couponWarnings.map((warning, index) => (
                      <Error data-testid="input-error" key={index}>
                        {warning ===
                        DISCOUNT_ERROR_CODES.CART_ERROR_APP_COUPON_ONLY ? (
                          <Trans>
                            The discount code applied can be used on app only.
                            <br />
                            Please login to the{" "}
                            <InlineHyperLink
                              target="blank"
                              asLink
                              href="http://onelink.to/mmzf9r"
                              variant="inherit"
                              color="inherit"
                              hasUnderline
                            >
                              Otrium app
                            </InlineHyperLink>{" "}
                            to use this code.
                          </Trans>
                        ) : (
                          warning
                        )}
                      </Error>
                    ))}
                  </div>
                </Flex>
              ) : (
                <ButtonAddCoupon
                  data-testid="cart-add-coupon-button"
                  onPress={() => setShowCouponInput(true)}
                >
                  <Promotional />
                  <Text
                    ml={2}
                    fontSize={1}
                    fontWeight="bold"
                    lineHeight={`${theme.fontSizes[3]}px`}
                    letterSpacing="0.004em"
                    as="span"
                    verticalAlign="middle"
                  >
                    <Trans>Add promo code</Trans>
                  </Text>
                </ButtonAddCoupon>
              )}
            </Flex>
          )}
          {checkoutMessage && (
            <Box mt={3}>
              <AlertBanner variant="neutral">
                <Text letterSpacing={0}>{checkoutMessage}</Text>
              </AlertBanner>
            </Box>
          )}
          {!isMobile && (
            <Box my={4}>
              <ButtonCheckout
                dataTestid="cart-continue-to-checkout-bottom-a"
                disabled={hasOutOfStockItems}
                onClick={() => setIsOpen(true)}
              />
            </Box>
          )}
          <Box my={4}>
            <PaymentMethodsInfo />
          </Box>
        </CartOrderSummaryContent>
      </Box>

      {!isEmptyCart && isMobile && (
        <Box mt={3}>
          <RRPFootnote />
        </Box>
      )}
    </>
  );
};

export default CartViewOrderSummary;
